import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useI18n } from '../../hooks/useI18n'
import { useCare } from '../../hooks/useCare'
import { useToast } from '../../contexts/ToastContext'
import { CareCase } from '../../types/user'
import Avatar from '../common/Avatar'

interface MainLayoutProps {
  children: React.ReactNode
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [showCaseSelector, setShowCaseSelector] = useState(false)
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false)
  const [showJoinCaseModal, setShowJoinCaseModal] = useState(false)
  const [showCaseManagementMenu, setShowCaseManagementMenu] = useState(false)
  const { user, userProfile } = useAuth()
  const { t } = useI18n()
  const { currentCase, userCases, switchToCase, permissions, createCase, joinWithCode, loadUserCases, deleteCase } = useCare()
  const location = useLocation()
  const navigate = useNavigate()
  const toast = useToast()

  // Close case selector and modals when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showCaseSelector && !(event.target as Element).closest('.case-selector')) {
        setShowCaseSelector(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [showCaseSelector])

  // Close modals with ESC key
  React.useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showCaseManagementMenu) setShowCaseManagementMenu(false)
        if (showCreateCaseModal) setShowCreateCaseModal(false)
        if (showJoinCaseModal) setShowJoinCaseModal(false)
      }
    }
    document.addEventListener('keydown', handleEscKey)
    return () => document.removeEventListener('keydown', handleEscKey)
  }, [showCaseManagementMenu, showCreateCaseModal, showJoinCaseModal])



  const handleCaseSwitch = async (caseId: string) => {
    try {
      await switchToCase(caseId)
      setShowCaseSelector(false)
    } catch (error) {
      console.error('Error switching case:', error)
    }
  }

  const handleNavigation = (path: string) => {
    navigate(path)
  }

  // Componente para crear caso
  const CreateCaseModal = () => {
    const [loading, setLoading] = useState(false)
    const [formData, setFormData] = useState({
      careFor: 'myself' as 'myself' | 'someone' | 'pet',
      caseName: '',
      profileName: '',
      profileAge: '',
      profileGender: '' as 'male' | 'female' | 'other',
      description: ''
    })

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      if (!formData.profileName) {
        toast.error('Error', 'Por favor completa el nombre')
        return
      }

      setLoading(true)
      try {
        // Generar nombre del caso automáticamente
        let caseName = formData.caseName
        if (!caseName) {
          if (formData.careFor === 'myself') {
            caseName = 'Mis Medicamentos'
          } else if (formData.careFor === 'someone') {
            caseName = `Medicamentos de ${formData.profileName}`
          } else {
            caseName = `Cuidado de ${formData.profileName}`
          }
        }

        const newCase: Omit<CareCase, 'id' | 'adminId' | 'createdAt' | 'updatedAt'> = {
          name: caseName,
          description: formData.description,
          profileType: formData.careFor === 'pet' ? 'pet' : 'human',
          profileInfo: {
            name: formData.profileName,
            age: formData.profileAge ? parseInt(formData.profileAge) : undefined,
            gender: formData.profileGender || undefined,
            avatarSeed: formData.profileName,
            avatarStyle: 'avataaars'
          },
          isActive: true,
          settings: {
            allowGuestAccess: false,
            requireApprovalForNewMembers: true,
            notificationSettings: {
              adminGetsAllNotifications: true,
              membersCanReceiveNotifications: true
            }
          }
        }

        await createCase(newCase)
        await loadUserCases()

        toast.success('¡Éxito!', 'Caso creado exitosamente')
        setShowCreateCaseModal(false)

        // Reset form
        setFormData({
          careFor: 'myself',
          caseName: '',
          profileName: '',
          profileAge: '',
          profileGender: 'other',
          description: ''
        })
      } catch (error) {
        console.error('Error creating case:', error)
        toast.error('Error', 'No se pudo crear el caso. Inténtalo de nuevo.')
      } finally {
        setLoading(false)
      }
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Crear Nuevo Caso
            </h2>
            <button
              onClick={() => setShowCreateCaseModal(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                ¿Para quién es este caso?
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="careFor"
                    value="myself"
                    checked={formData.careFor === 'myself'}
                    onChange={(e) => setFormData({ ...formData, careFor: e.target.value as any })}
                    className="mr-2"
                  />
                  <span>Para mí mismo</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="careFor"
                    value="someone"
                    checked={formData.careFor === 'someone'}
                    onChange={(e) => setFormData({ ...formData, careFor: e.target.value as any })}
                    className="mr-2"
                  />
                  <span>Para cuidar a alguien</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="careFor"
                    value="pet"
                    checked={formData.careFor === 'pet'}
                    onChange={(e) => setFormData({ ...formData, careFor: e.target.value as any })}
                    className="mr-2"
                  />
                  <span>Para mi mascota</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {formData.careFor === 'myself' ? 'Tu nombre' :
                  formData.careFor === 'someone' ? 'Nombre de la persona' : 'Nombre de la mascota'}
              </label>
              <input
                type="text"
                value={formData.profileName}
                onChange={(e) => setFormData({ ...formData, profileName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder={formData.careFor === 'myself' ? 'Tu nombre completo' :
                  formData.careFor === 'someone' ? 'Ej: María García' : 'Ej: Max'}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Edad (opcional)
                </label>
                <input
                  type="number"
                  value={formData.profileAge}
                  onChange={(e) => setFormData({ ...formData, profileAge: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Ej: 65"
                  min="1"
                  max={formData.careFor === 'pet' ? "30" : "120"}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Género (opcional)
                </label>
                <select
                  value={formData.profileGender}
                  onChange={(e) => setFormData({ ...formData, profileGender: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="other">Otro</option>
                  <option value="male">Masculino</option>
                  <option value="female">Femenino</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Descripción (opcional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Información adicional sobre el caso..."
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowCreateCaseModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                disabled={loading}
              >
                {loading ? 'Creando...' : 'Crear Caso'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Componente para unirse a caso
  const JoinCaseModal = () => {
    const [loading, setLoading] = useState(false)
    const [inviteCode, setInviteCode] = useState('')

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      const codeToUse = inviteCode.trim().toUpperCase()

      if (!codeToUse) {
        toast.error('Error', 'Por favor ingresa el código de invitación')
        return
      }

      setLoading(true)
      try {
        await joinWithCode(codeToUse)
        await loadUserCases()

        toast.success('¡Éxito!', '¡Te has unido al caso exitosamente!')
        setShowJoinCaseModal(false)
        setInviteCode('')
      } catch (error: any) {
        console.error('Error joining case:', error)
        toast.error('Error', error.message || 'Código de invitación inválido o expirado')
      } finally {
        setLoading(false)
      }
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Unirse a un Caso
            </h2>
            <button
              onClick={() => setShowJoinCaseModal(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Código de Invitación
              </label>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-center text-lg font-mono tracking-wider"
                placeholder="CARE-2024-ABC123"
                required
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Ingresa el código de invitación que recibiste
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-200">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Al unirte, podrás colaborar en el cuidado y gestión de medicamentos</span>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowJoinCaseModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                disabled={loading || !inviteCode.trim()}
              >
                {loading ? 'Uniéndose...' : 'Unirse al Caso'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }



  const getIcon = (iconName: string) => {
    const iconClass = "w-5 h-5 transition-transform duration-150 ease-in-out"
    const icons = {
      home: (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      pills: (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
      calendar: (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      progress: (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      user: (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      team: (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    }
    return icons[iconName as keyof typeof icons] || null
  }

  const navigation = [
    { path: '/', name: 'home', icon: 'home', permission: null },
    { path: '/medications', name: 'medications', icon: 'pills', permission: null },
    { path: '/calendar', name: 'calendar', icon: 'calendar', permission: 'canViewHistory' },
    { path: '/progress', name: 'progress', icon: 'progress', permission: 'canViewHistory' },
    { path: '/profile', name: 'profile', icon: 'user', permission: null }
  ].filter(item => !item.permission || permissions[item.permission as keyof typeof permissions])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('app-title')}
            </h1>
          </div>

          <div className="flex items-center space-x-4">
            {/* Caso Actual Label */}
            {currentCase && (
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden md:inline">
                Caso Actual:
              </span>
            )}

            {/* Case Selector */}
            {currentCase && (
              <div className="relative case-selector">
                <button
                  onClick={() => setShowCaseSelector(!showCaseSelector)}
                  className="flex items-center space-x-2 px-2 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <Avatar
                    user={{
                      type: currentCase.profileType === 'pet' ? 'pet' : 'human',
                      name: currentCase.profileInfo?.name || currentCase.name || 'Caso',
                      userId: currentCase.id, // ID único del caso actual
                      petAvatarConfig: currentCase.profileInfo?.petAvatarConfig,
                      avatarCustomization: currentCase.profileInfo?.avatarCustomization
                    }}
                    size="sm"
                  />
                  <span className="text-gray-900 dark:text-white font-medium">
                    {currentCase.name}
                  </span>
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>

                {showCaseSelector && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                    <div className="p-3">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                        Cambiar Caso
                      </h3>
                      <div className="space-y-1">
                        {userCases.map((careCase) => (
                          <button
                            key={careCase.id}
                            onClick={() => {
                              switchToCase(careCase.id)
                              setShowCaseSelector(false)
                            }}
                            className="w-full p-1 text-left hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          >
                            <div className="flex items-center space-x-2">
                              <Avatar
                                user={{
                                  type: careCase.profileType === 'pet' ? 'pet' : 'human',
                                  name: careCase.profileInfo?.name || careCase.name || 'Caso',
                                  userId: careCase.id, // ID único del caso
                                  petAvatarConfig: careCase.profileInfo?.petAvatarConfig,
                                  avatarCustomization: careCase.profileInfo?.avatarCustomization // Si existe configuración personalizada
                                }}
                                size="sm"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {careCase.name}
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                  {careCase.profileInfo?.name || (careCase as any).patientInfo?.name || 'Sin nombre'}
                                </div>
                              </div>
                              {careCase.id === currentCase.id && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                        {permissions.canViewUsers && (
                          <button
                            onClick={() => {
                              console.log('🔍 Navigating to /users')
                              console.log('📋 Current permissions:', permissions)
                              setShowCaseSelector(false)
                              navigate('/users')
                            }}
                            className="w-full text-left p-2 text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            Ver detalles del caso
                          </button>
                        )}
                        {!permissions.canViewUsers && (
                          <div className="p-2 text-xs text-gray-500 dark:text-gray-400 italic">
                            ℹ️ No tienes permisos para ver detalles del caso
                          </div>
                        )}


                        <div className="relative">
                          <button
                            onClick={() => {
                              setShowCaseSelector(false)
                              setShowCaseManagementMenu(true)
                            }}
                            className="w-full text-left p-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Crear o unirse a caso
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Modal de opciones para gestión de casos */}
      {showCaseManagementMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Gestionar Casos
              </h2>
              <button
                onClick={() => setShowCaseManagementMenu(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => {
                  setShowCaseManagementMenu(false)
                  setShowCreateCaseModal(true)
                }}
                className="w-full p-4 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Crear Nuevo Caso</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Configurar un nuevo caso de medicación
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setShowCaseManagementMenu(false)
                  setShowJoinCaseModal(true)
                }}
                className="w-full p-4 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Unirse a Caso Existente</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Usar código de invitación para unirse a un caso
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para crear caso */}
      {showCreateCaseModal && <CreateCaseModal />}

      {/* Modal para unirse a caso */}
      {showJoinCaseModal && <JoinCaseModal />}

      <div className="flex">
        {/* Desktop Sidebar */}
        <nav className="hidden md:flex md:flex-col md:w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 min-h-screen">
          {/* User Info Section */}
          <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <Avatar
                user={{
                  type: 'human',
                  name: userProfile?.name || user?.displayName || user?.email || 'Usuario',
                  userId: user?.uid || undefined, // ID único del usuario logueado
                  email: user?.email || undefined, // Email adicional para identificación
                  avatarCustomization: userProfile?.avatarCustomization // Configuración personalizada si existe
                }}
                size="md"
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                  {userProfile?.name || user?.displayName || 'Usuario'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
          </div>

          <div className="flex-1 px-4 py-6">
            <ul className="space-y-2">
              {navigation.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center px-4 py-2 text-xs font-medium rounded-md transition-all duration-150 ease-in-out transform active:scale-95 ${location.pathname === item.path
                        ? 'bg-primary text-white'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                  >
                    <span className="mr-3 active:scale-90 transition-transform duration-150 ease-in-out">{getIcon(item.icon)}</span>
                    {t(item.name)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </nav>



        {/* Main Content */}
        <main className="flex-1 h-screen overflow-hidden">
          <div className="h-full overflow-y-auto pb-20 md:pb-0">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-50">
        <div className="flex items-center px-4 py-2">
          {navigation.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex-1 flex flex-col items-center p-2 rounded-lg transition-all duration-150 ease-in-out transform active:scale-95 ${location.pathname === item.path
                  ? 'text-primary bg-primary/10'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
            >
              <span className="mb-1 active:scale-90 transition-transform duration-150 ease-in-out">{getIcon(item.icon)}</span>
              <span className="text-[10px] font-medium text-center">{t(item.name)}</span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  )
}

export default MainLayout 